import React, { useState } from 'react'
import { Store, MapPin, TrendingUp, TrendingDown, AlertTriangle, Search, Filter, ChevronDown, ChevronUp, BarChart3, Users, Package } from 'lucide-react'
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext'
import { useStorePerformanceDeepDive } from '../lib/data-service'
import { formatCurrency, formatNumber, formatPercent } from '../lib/utils'
import GlobalHeader from '../components/GlobalHeader'

export default function StorePerformanceDeepDive() {
  const { selectedFiscalWeek: fiscalWeek } = useFiscalWeekContext()
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState('revenue')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)
  const [topFilter, setTopFilter] = useState<'growers' | 'decliners' | null>(null)
  const [expandedSections, setExpandedSections] = useState({
    cohorts: true,
    geography: true,
    inventory: true,
    ranking: true
  })

  const pageSize = 25

  const { data, isLoading, error } = useStorePerformanceDeepDive(
    fiscalWeek,
    searchQuery,
    sortField,
    sortDir,
    page,
    pageSize,
    topFilter
  )

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
    setPage(1)
  }

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const getRiskBadgeColor = (flags: string[]) => {
    if (flags.includes('chronic_oos')) return 'bg-red-100 text-red-800 border-red-200'
    if (flags.includes('low_coverage')) return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    return 'bg-green-100 text-green-800 border-green-200'
  }

  const getYoyChipColor = (yoy: number | null) => {
    if (!yoy) return 'bg-gray-100 text-gray-600'
    if (yoy > 0) return 'bg-green-100 text-green-700'
    return 'bg-red-100 text-red-700'
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <p className="text-destructive">Error loading store performance data: {error.message}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Global Header with Date Picker */}
      <GlobalHeader
        title="Store Performance"
        subtitle="Individual store metrics and risk assessment"
        isLoading={isLoading}
      />

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Store Cohorts Section */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-6">
            <button
              onClick={() => toggleSection('cohorts')}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-foreground">Store Cohorts Analysis</h2>
              </div>
              {expandedSections.cohorts ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>

            {expandedSections.cohorts && data?.cohorts && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Comp Stores */}
                <div className="bg-secondary/30 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
                    Comparable Stores
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-foreground">Revenue</span>
                      <span className="text-lg font-semibold text-foreground">
                        {formatCurrency(data.cohorts.comp_revenue)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-foreground">Share of Total</span>
                      <span className="text-sm font-medium text-foreground">
                        {formatPercent(data.cohorts.comp_share)}
                      </span>
                    </div>
                    {data.cohorts.comp_yoy_pct !== null && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-foreground">YoY Growth</span>
                        <span className={`text-sm font-medium px-2 py-1 rounded-full ${getYoyChipColor(data.cohorts.comp_yoy_pct)}`}>
                          {data.cohorts.comp_yoy_pct > 0 ? '+' : ''}{formatPercent(data.cohorts.comp_yoy_pct)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Non-Comp Stores */}
                <div className="bg-secondary/30 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
                    Non-Comparable Stores
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-foreground">Revenue</span>
                      <span className="text-lg font-semibold text-foreground">
                        {formatCurrency(data.cohorts.noncomp_revenue)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-foreground">Share of Total</span>
                      <span className="text-sm font-medium text-foreground">
                        {formatPercent(data.cohorts.noncomp_share)}
                      </span>
                    </div>
                    {data.cohorts.noncomp_yoy_pct !== null && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-foreground">YoY Growth</span>
                        <span className={`text-sm font-medium px-2 py-1 rounded-full ${getYoyChipColor(data.cohorts.noncomp_yoy_pct)}`}>
                          {data.cohorts.noncomp_yoy_pct > 0 ? '+' : ''}{formatPercent(data.cohorts.noncomp_yoy_pct)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Geographic Insights Section */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-6">
            <button
              onClick={() => toggleSection('geography')}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-foreground">Geographic Insights</h2>
              </div>
              {expandedSections.geography ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>

            {expandedSections.geography && data?.geo && (
              <div className="mt-6 space-y-6">
                {/* Top & Bottom States */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-green-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Top Performing States
                    </h3>
                    <div className="space-y-2">
                      {data.geo.top_states.slice(0, 5).map((state, index) => (
                        <div key={state.state} className="flex justify-between items-center">
                          <span className="text-sm font-medium text-green-900">
                            #{index + 1} {state.state}
                          </span>
                          <span className="text-sm font-semibold text-green-700">
                            +{formatPercent(Math.abs(state.yoy_rev_pct))}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-red-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <TrendingDown className="w-4 h-4" />
                      Underperforming States
                    </h3>
                    <div className="space-y-2">
                      {data.geo.bottom_states.slice(0, 5).map((state, index) => (
                        <div key={state.state} className="flex justify-between items-center">
                          <span className="text-sm font-medium text-red-900">
                            #{index + 1} {state.state}
                          </span>
                          <span className="text-sm font-semibold text-red-700">
                            {formatPercent(state.yoy_rev_pct)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* State Performance Table */}
                <div className="bg-secondary/20 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
                    All States Performance
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border/60">
                          <th className="text-left py-2 font-medium text-muted-foreground">State</th>
                          <th className="text-right py-2 font-medium text-muted-foreground">Revenue</th>
                          <th className="text-right py-2 font-medium text-muted-foreground">YoY %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.geo.by_state.map((state) => (
                          <tr key={state.state} className="border-b border-border/30">
                            <td className="py-2 font-medium text-foreground">{state.state}</td>
                            <td className="py-2 text-right text-foreground">{formatCurrency(state.revenue)}</td>
                            <td className="py-2 text-right">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getYoyChipColor(state.yoy_rev_pct)}`}>
                                {state.yoy_rev_pct > 0 ? '+' : ''}{formatPercent(state.yoy_rev_pct)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Inventory Health Section */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-6">
            <button
              onClick={() => toggleSection('inventory')}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-foreground">Inventory Health Monitoring</h2>
              </div>
              {expandedSections.inventory ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>

            {expandedSections.inventory && data?.inventoryHealth && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Low Coverage Stores */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-yellow-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Low Coverage Risk ({data.inventoryHealth.lowCoverage.length} stores)
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {data.inventoryHealth.lowCoverage.slice(0, 10).map((store) => (
                      <div key={store.store_id} className="flex justify-between items-center">
                        <span className="text-sm text-yellow-900 flex-1" title={store.store_name}>
                          {store.store_name.substring(0, 25)}...
                        </span>
                        <span className="text-xs font-medium text-yellow-700 bg-yellow-100 px-2 py-1 rounded">
                          {store.wos_store?.toFixed(1)}w
                        </span>
                      </div>
                    ))}
                    {data.inventoryHealth.lowCoverage.length > 10 && (
                      <p className="text-xs text-yellow-700 italic">
                        +{data.inventoryHealth.lowCoverage.length - 10} more stores
                      </p>
                    )}
                  </div>
                </div>

                {/* Chronic OOS Stores */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-red-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Chronic Out-of-Stock ({data.inventoryHealth.chronicOOS.length} stores)
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {data.inventoryHealth.chronicOOS.slice(0, 10).map((store) => (
                      <div key={store.store_id} className="flex justify-between items-center">
                        <span className="text-sm text-red-900 flex-1" title={store.store_name}>
                          {store.store_name.substring(0, 25)}...
                        </span>
                        <div className="text-xs">
                          <span className="font-medium text-red-700 bg-red-100 px-2 py-1 rounded mr-1">
                            {store.outs_total} outs
                          </span>
                          <span className="text-red-600">
                            {store.velocity_avg_5w?.toFixed(1)} vel
                          </span>
                        </div>
                      </div>
                    ))}
                    {data.inventoryHealth.chronicOOS.length > 10 && (
                      <p className="text-xs text-red-700 italic">
                        +{data.inventoryHealth.chronicOOS.length - 10} more stores
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Top Performers & Decliners */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-6">
            <button
              onClick={() => toggleSection('ranking')}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-foreground">Store Rankings</h2>
              </div>
              {expandedSections.ranking ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>

            {expandedSections.ranking && data?.ranking && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Top Growers */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-green-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Top Revenue Growers
                  </h3>
                  <div className="space-y-2">
                    {data.ranking.top_growers.map((store, index) => (
                      <div key={store.store_id} className="flex justify-between items-center">
                        <span className="text-sm text-green-900 flex-1" title={store.store_name}>
                          #{index + 1} {store.store_name.substring(0, 20)}...
                        </span>
                        <span className="text-sm font-semibold text-green-700">
                          +{formatPercent(Math.abs(store.yoy_rev_pct))}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Decliners */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-red-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <TrendingDown className="w-4 h-4" />
                    Revenue Decliners
                  </h3>
                  <div className="space-y-2">
                    {data.ranking.top_decliners.map((store, index) => (
                      <div key={store.store_id} className="flex justify-between items-center">
                        <span className="text-sm text-red-900 flex-1" title={store.store_name}>
                          #{index + 1} {store.store_name.substring(0, 20)}...
                        </span>
                        <span className="text-sm font-semibold text-red-700">
                          {formatPercent(store.yoy_rev_pct)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Store Performance Table */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-foreground">Store Performance Details</h2>
              
              {/* Filters */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search stores..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                
                <select
                  value={topFilter || ''}
                  onChange={(e) => setTopFilter(e.target.value as 'growers' | 'decliners' | null || null)}
                  className="px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">All Stores</option>
                  <option value="growers">Top Growers</option>
                  <option value="decliners">Decliners</option>
                </select>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60">
                    <th className="text-left py-3 font-medium text-muted-foreground">
                      <button
                        onClick={() => handleSort('store_name')}
                        className="flex items-center gap-1 hover:text-foreground"
                      >
                        Store Name
                        {sortField === 'store_name' && (sortDir === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                      </button>
                    </th>
                    <th className="text-left py-3 font-medium text-muted-foreground">State</th>
                    <th className="text-right py-3 font-medium text-muted-foreground">
                      <button
                        onClick={() => handleSort('revenue')}
                        className="flex items-center gap-1 hover:text-foreground ml-auto"
                      >
                        Revenue
                        {sortField === 'revenue' && (sortDir === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                      </button>
                    </th>
                    <th className="text-right py-3 font-medium text-muted-foreground">
                      <button
                        onClick={() => handleSort('units')}
                        className="flex items-center gap-1 hover:text-foreground ml-auto"
                      >
                        Units
                        {sortField === 'units' && (sortDir === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                      </button>
                    </th>
                    <th className="text-right py-3 font-medium text-muted-foreground">
                      <button
                        onClick={() => handleSort('yoy_rev_pct')}
                        className="flex items-center gap-1 hover:text-foreground ml-auto"
                      >
                        Revenue YoY
                        {sortField === 'yoy_rev_pct' && (sortDir === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                      </button>
                    </th>
                    <th className="text-right py-3 font-medium text-muted-foreground">
                      <button
                        onClick={() => handleSort('yoy_units_pct')}
                        className="flex items-center gap-1 hover:text-foreground ml-auto"
                      >
                        Units YoY
                        {sortField === 'yoy_units_pct' && (sortDir === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                      </button>
                    </th>
                    <th className="text-right py-3 font-medium text-muted-foreground">WOS</th>
                    <th className="text-left py-3 font-medium text-muted-foreground">Risk Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    Array.from({ length: 10 }).map((_, i) => (
                      <tr key={i} className="border-b border-border/30">
                        <td className="py-3"><div className="h-4 bg-secondary/50 rounded animate-pulse"></div></td>
                        <td className="py-3"><div className="h-4 bg-secondary/50 rounded animate-pulse w-8"></div></td>
                        <td className="py-3"><div className="h-4 bg-secondary/50 rounded animate-pulse w-16 ml-auto"></div></td>
                        <td className="py-3"><div className="h-4 bg-secondary/50 rounded animate-pulse w-12 ml-auto"></div></td>
                        <td className="py-3"><div className="h-4 bg-secondary/50 rounded animate-pulse w-16 ml-auto"></div></td>
                        <td className="py-3"><div className="h-4 bg-secondary/50 rounded animate-pulse w-16 ml-auto"></div></td>
                        <td className="py-3"><div className="h-4 bg-secondary/50 rounded animate-pulse w-12 ml-auto"></div></td>
                        <td className="py-3"><div className="h-4 bg-secondary/50 rounded animate-pulse w-20"></div></td>
                      </tr>
                    ))
                  ) : (
                    data?.rows.map((store) => (
                      <tr key={store.store_id} className="border-b border-border/30 hover:bg-secondary/20">
                        <td className="py-3 font-medium text-foreground max-w-xs">
                          <div className="truncate" title={store.store_name}>
                            {store.store_name}
                          </div>
                        </td>
                        <td className="py-3 text-muted-foreground font-mono text-xs">
                          {store.state}
                        </td>
                        <td className="py-3 text-right font-medium text-foreground">
                          {formatCurrency(store.revenue)}
                        </td>
                        <td className="py-3 text-right text-foreground">
                          {formatNumber(store.units)}
                        </td>
                        <td className="py-3 text-right">
                          {store.yoy_rev_pct !== null ? (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getYoyChipColor(store.yoy_rev_pct)}`}>
                              {store.yoy_rev_pct > 0 ? '+' : ''}{formatPercent(store.yoy_rev_pct)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-3 text-right">
                          {store.yoy_units_pct !== null ? (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getYoyChipColor(store.yoy_units_pct)}`}>
                              {store.yoy_units_pct > 0 ? '+' : ''}{formatPercent(store.yoy_units_pct)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-3 text-right text-muted-foreground">
                          {store.wos_store ? `${store.wos_store.toFixed(1)}w` : '—'}
                        </td>
                        <td className="py-3">
                          <div className="flex flex-wrap gap-1">
                            {store.flags.map((flag) => (
                              <span
                                key={flag}
                                className={`px-2 py-1 rounded-full text-xs font-medium border ${getRiskBadgeColor(store.flags)}`}
                              >
                                {flag.replace('_', ' ')}
                              </span>
                            ))}
                            {store.flags.length === 0 && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                healthy
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data && data.totalRowCount > pageSize && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-muted-foreground">
                  Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, data.totalRowCount)} of {data.totalRowCount} stores
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-2 border border-border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-2 text-sm">
                    Page {page} of {Math.ceil(data.totalRowCount / pageSize)}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(Math.ceil(data.totalRowCount / pageSize), page + 1))}
                    disabled={page >= Math.ceil(data.totalRowCount / pageSize)}
                    className="px-3 py-2 border border-border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
